import React from 'react';
import styled from 'types/theme.types';
import { css } from '@emotion/react';
import { FormattedMessage } from 'react-intl';

import StyledInfo from 'components/common/info';
import useIsMobile from 'hooks/use-is-mobile';
import { typeStyle } from 'constants/typography';

const DEFAULT_FIELD_MARGIN_RIGHT = 30;

/**
 * Despite living here, `modal.tsx` is its only importer - it is the modal's subtitle line.
 */
const Explanation = styled.div`
  ${typeStyle.bodyMd}
  color: var(--text-body);
`;

const FieldRow = styled.div<{ addMargin?: boolean; zeroTopMargin?: boolean; zeroBottomMargin?: boolean }>`
  display: flex;
  width: 100%;
  ${({ addMargin }) => addMargin && 'margin-right: ' + DEFAULT_FIELD_MARGIN_RIGHT + 'px'}
  ${({ zeroTopMargin }) => zeroTopMargin && 'margin-top: 0px;'}
  ${({ zeroBottomMargin }) => zeroBottomMargin && 'margin-bottom: 0px;'}
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const FieldAndLabel = styled.div<{ width?: number; space?: number; height?: number; position?: string }>`
  width: ${({ width }) => (width ? width + '%' : '100%')};
  height: ${({ height }) => (height ? height + '%' : 'auto')};
  position: ${({ position }) => position || 'inherit'};
  display: flex;
  flex-direction: column;

  textarea {
    height: 100%;
    padding-top: 5px;
    padding-bottom: 5px;
  }

  &:not(:first-of-type) {
    margin-left: ${props => (props.hasOwnProperty('space') ? props.space : DEFAULT_FIELD_MARGIN_RIGHT)}px;
  }
`;

const StyledLabel = styled.label`
  /* Flex, not block: the optional suffix is a sibling of the flex text row, so under block it dropped
     to its own line and sat flush on the field below (UI-polish review, "Create New Project"). Figma's
     Input Label Vertical draws label, suffix and marker on one line. */
  ${({ theme }) => css`
    ${typeStyle.labelMd}
    color: var(--component-field-label);
    display: flex;
    align-items: baseline;
    gap: var(--spacing-xxxs);
    margin-bottom: ${theme.spacing.xs};
  `}
`;

const LabelTextRow = styled.div`
  display: flex;
`;

const OptionalLabel = styled.span`
  color: var(--text-secondary);
`;

const Label = ({
  id,
  text,
  values,
  optional,
  mandatory,
  ...props
}: {
  id?: string;
  text?: string;
  values?: Record<string, any>;
  optional?: boolean;
  mandatory?: boolean;
}) => (
  <StyledLabel {...props}>
    <LabelTextRow>
      {text ? text : <FormattedMessage id={id!} values={values} />}
      {mandatory && <div data-e2e={`${id || text}.mandatory`}>&nbsp;*</div>}
    </LabelTextRow>
    {optional && (
      <OptionalLabel>
        <FormattedMessage id="templates.add.new.optional" />
      </OptionalLabel>
    )}
  </StyledLabel>
);

export interface LabelTextProps {
  id: string;
  values?: Record<string, any>;
  info?: InfoProps;
  'data-e2e'?: string;
}

export interface InfoProps {
  id?: string;
  values?: Record<string, any>;
}

const LabelText = ({ id, values, info, 'data-e2e': dataE2e }: LabelTextProps) => (
  <span data-e2e={dataE2e}>
    <FormattedMessage id={id} values={values} />
    {info && (
      <Info
        information={<FormattedMessage {...info} />}
        onClick={e => {
          e.preventDefault();
        }}
      />
    )}
  </span>
);

const DesktopLabelText = (props: LabelTextProps) => {
  const { isDesktop } = useIsMobile();
  const { info, ...restProps } = props;
  return <LabelText {...restProps} info={isDesktop ? info : undefined} />;
};

const LabelAndIcon = styled.div`
  display: flex;
  align-items: center;
`;

const Info = ({
  information,
  withoutStyle,
  alignTop = true,
  className,
  onClick
}: {
  information: string | React.ReactNode;
  withoutStyle?: boolean;
  alignTop?: boolean;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const iconStyle = {
    marginLeft: '5px',
    ...(alignTop && { marginBottom: 'auto' })
  };

  return (
    <StyledInfo
      onClick={onClick}
      className={className}
      tooltip={<TooltipMessage>{information}</TooltipMessage>}
      style={withoutStyle ? undefined : iconStyle}
    />
  );
};

const TooltipMessage = styled.div`
  white-space: pre-line;
`;

const ErrorMessage = styled.div<{ errorShouldNotTakeSpace?: boolean; hideWhenEmpty?: boolean }>`
  ${({ theme }) => css`
    ${typeStyle.captionMd}
    margin: ${theme.scale['50']};
    color: var(--component-field-text-error);
    /* Reserved so the field does not jump when a message appears. */
    min-height: ${theme.scale['500']};
  `}

  ${({ errorShouldNotTakeSpace }) => errorShouldNotTakeSpace && 'position: absolute; top: 100%;'}
  ${({ hideWhenEmpty }) => hideWhenEmpty && '&:empty { display: none; }'}
`;

const CommonErrorMessage = styled.div`
  height: 21px;
  ${typeStyle.captionMd}
  color: var(--surface-error-action);
`;

const Container = styled.div`
  display: grid;
  grid-template-rows: repeat(1fr, 3);
  grid-row-gap: 9px;
  padding: 0 8px 0 8px;
`;

const Field = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  align-items: center;
  grid-row-gap: 2px;
  ${typeStyle.bodyMd}
`;

const EllipsisContainer = styled.div<{ maxWidth?: number }>`
  max-width: ${({ maxWidth }) => maxWidth + 'px'};
  width: max-content;
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

export {
  Explanation,
  ErrorMessage,
  CommonErrorMessage,
  Info,
  LabelAndIcon,
  Label,
  OptionalLabel,
  StyledLabel,
  FieldAndLabel,
  Row,
  FieldRow,
  Container,
  Field,
  EllipsisContainer,
  LabelText,
  DesktopLabelText
};
